import { FocusMonitor } from '@angular/cdk/a11y'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit, Optional, Self,
  SimpleChanges,
} from '@angular/core'
import { FormGroupDirective, NgControl, NgForm } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatFormFieldControl } from '@angular/material/form-field'
import { ClinicService, ProductCategory, ProductModel } from '@mmx/shared'
import { filter, intersection } from 'lodash'
import { takeUntil } from 'rxjs'

import { ChipsSelectorInputComponent } from '../chips-selector'
import { ProductsSelectorDialogComponent } from './products-selector-dialog.component'

@Component({
  selector: 'mmx-products-selector-input',
  templateUrl: '../chips-selector/chips-selector.component.html',
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: ProductsSelectorInputComponent,
    },
  ],
  standalone: false,
})
export class ProductsSelectorInputComponent
  extends ChipsSelectorInputComponent<ProductModel>
  implements OnInit, OnChanges
{
  @Input() hideControls = false
  @Input() categories: (string | ProductCategory)[]
  @Input() includeCategories: boolean = true
  protected _placeholder = 'Procedures'

  @Input()
  get quickAddByCodeEnabled() {
    return this._quickAdd
  }
  set quickAddByCodeEnabled(enabled: boolean) {
    this._quickAdd = coerceBooleanProperty(enabled)
    this.stateChanges.next()
  }
  protected _quickAdd = false

  private products: ProductModel[] = []

  private fullProductSet: ProductModel[]

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() protected parentForm: NgForm,
    @Optional() protected parentFormGroup: FormGroupDirective,
    protected fm: FocusMonitor,
    protected elRef: ElementRef,
    private clinicService: ClinicService,
    private dialog: MatDialog,
  ) {
    super(ngControl, parentForm, parentFormGroup, fm, elRef)
  }

  ngOnInit() {
    super.ngOnInit()

    // inactive procedures are loaded so a saved one keeps resolving, isSelectable keeps them
    // out of the list of things that can be picked
    this.clinicService.getProducts({ includeInactive: true })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((products) => {
        this.products = products
        this.filterProducts()
      })

    // TODO: Implement query by keyword from the backend
    // this.productsCtrl.valueChanges
    //   .pipe(
    //     startWith(''),
    //     debounceTime(500),
    //     distinctUntilChanged(),
    //     switchMap((query) => this.fullProductSet?.length ? of(this.fullProductSet) : this.clinicService.getProducts({ query }) ),
    //     takeUntil(this.onDestroy$))
    //   .subscribe((products) => {
    //     this.products = products

    //     // If there's less than 1000 products in the full unfiltered list, cache the result to reduce api calls
    //     if (!this.fullProductSet?.length && this.products.length < 1000 && !this.productsCtrl.value) {
    //       this.fullProductSet = products
    //     }

    //     this.filterProducts()
    //   })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.categories || changes.includeCategories) {
      this.filterProducts()
    }
  }

  filterProducts() {
    let products = this.products

    if (this.categories?.length > 0) {
      const categories = this.categories.map((cat) =>
        cat instanceof ProductCategory ? cat.id : cat,
      )
      products = filter(
        products,
        (p) => {
          return this.includeCategories ?
            intersection(categories, p.categories).length > 0 : intersection(categories, p.categories).length === 0
        },
      )
    }

    this.setAvailableItems(products, ['code', 'category', 'name'])
  }

  protected override isSelectable(product: ProductModel): boolean {
    return !product.inactive
  }

  override getItemLabel(product: ProductModel): string {
    const label = super.getItemLabel(product)
    return product.inactive ? `${label} (inactive)` : label
  }

  protected onItemSelected(product: ProductModel): void {
    if (this._quickAdd && product.code && product.code.length > 0) {
      // selectableItems, not availableItems: the quick-add list opens with every option ticked,
      // so a retired duplicate here would be attached by the default click
      const productsWithSameCode = this.selectableItems.filter(
        (p) => p.id !== product.id && p.code === product.code,
      )
      if (productsWithSameCode.length > 0) {
        const dialogRef = this.dialog.open(ProductsSelectorDialogComponent, {
          width: '550px',
          data: {
            products: productsWithSameCode,
          },
        })

        dialogRef.afterClosed().subscribe((selected: ProductModel[] | undefined) => {
          // undefined when the dialog is dismissed with escape or a backdrop click
          if (selected == null) {
            return
          }

          const componentValue = this.value || []
          selected.forEach((p) => componentValue.push(p))
          this.value = componentValue
        })
      }
    }
  }
}
